import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { useUpdateEffect } from '../../hooks/useCustomEffects';
import { getCompanies } from '../../services/companiesService';
import { IdName } from '../../types/app';
import { Company } from '../../types/companies';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  company?: IdName;
  companies?: IdName[];
  selectedCompanyId?: string;
  selectedCompanyIds?: string[];
  onChangeCompanyId?: (companyId?: string) => void;
  onChangeCompany?: (company?: Company) => void;
  onChangeCompanyIds?: (companyIds?: string[]) => void;
  onChangeCompanies?: (companies?: Company[]) => void;
  isMulti?: boolean;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
  smallContainer?: boolean;
  dropdownOffset?: string;
};
export function CompanySelect({
  selectedCompanyId,
  onChangeCompanyId,
  onChangeCompany,
  selectedCompanyIds,
  onChangeCompanyIds,
  onChangeCompanies,
  company,
  companies,
  isFilter = true,
  label = 'Compañia:',
  emptyOptionText = 'Seleciona una compañia',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  isMulti = false,
  maxDropdownHeight,
  smallContainer,
  dropdownOffset,
}: Readonly<Props>) {
  const [companiesList, setCompaniesList] = useState<Company[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue<string | undefined>(undefined, 350);
  const [selectedCompanyOption, setSelectedCompanyOption] = useState<{ id: string; label: string }>();
  const [selectedCompanyOptions, setSelectedCompanyOptions] = useState<{ id: string; label: string }[]>();

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadCompanies = async () => {
      startLoading();
      const { paginatedResponse, error } = await getCompanies(page, debouncedSearch, 'active');
      if (!error && paginatedResponse) {
        if (page > 1 && companiesList) {
          setCompaniesList([...companiesList, ...paginatedResponse.items]);
          setHasNextPage(paginatedResponse.hasNext);
        } else {
          setCompaniesList(paginatedResponse.items);
          setHasNextPage(paginatedResponse.hasNext);
        }
      } else {
        setCompaniesList(undefined);
        setHasNextPage(false);
      }
      stopLoading();
    };
    loadCompanies();
  }, [page]);

  const companyOptions = useMemo(() => {
    if (!companiesList) return;
    const coreCompaniesOptions = companiesList.map((company) => ({ id: company._id, label: company.name }));
    const companiesOptions = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreCompaniesOptions,
        ]
      : coreCompaniesOptions;

    if (company) {
      if (!companiesOptions.find((c) => c.id === company._id))
        companiesOptions.push({
          id: company._id,
          label: company.name,
        });
    } else if (companies) {
      companies.forEach((company) => {
        if (!companiesOptions.find((d) => d.id === company._id))
          companiesOptions.push({
            id: company._id,
            label: company.name,
          });
      });
    }
    if (selectedCompanyOption) {
      if (!companiesOptions.find((c) => c.id === selectedCompanyOption.id))
        companiesOptions.push(selectedCompanyOption);
    } else if (selectedCompanyOptions) {
      selectedCompanyOptions.forEach((company) => {
        if (!companiesOptions.find((d) => d.id === company.id) && company.id) companiesOptions.push(company);
      });
    }
    return companiesOptions;
  }, [companiesList]);

  const onCompanyChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeCompanyId && onChangeCompanyId(params.option.id as string);
      onChangeCompany &&
        onChangeCompany({
          _id: params.option?.id,
          name: params.option?.label,
        } as Company);
    } else {
      onChangeCompanyId && onChangeCompanyId(undefined);
      onChangeCompany && onChangeCompany(undefined);
    }
    setSelectedCompanyOption(params.option as { id: string; label: string });
    setDebouncedSearch(undefined);
  };

  const onCompaniesChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeCompanyIds &&
        onChangeCompanyIds(params.value.map((val) => val.id).filter((id) => id !== undefined) as string[]);
      onChangeCompanies &&
        onChangeCompanies(
          params.value
            .map((val) => ({ _id: val.id, name: val.label }))
            .filter((option) => option._id !== undefined) as Company[]
        );
    } else {
      onChangeCompanyIds && onChangeCompanyIds(undefined);
      onChangeCompanies && onChangeCompanies(undefined);
    }
    setSelectedCompanyOptions(
      params.value.map((val) => val).filter((val) => val.id !== undefined) as { id: string; label: string }[]
    );
    setDebouncedSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceCallback(function (term: string) {
    setDebouncedSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={companyOptions}
      value={
        isMulti
          ? showEmptyOption || selectedCompanyIds !== undefined
            ? selectedCompanyIds?.map((companyId) => ({ id: companyId }))
            : undefined
          : showEmptyOption || selectedCompanyId !== undefined
          ? [{ id: selectedCompanyId }]
          : undefined
      }
      onChange={isMulti ? onCompaniesChange : onCompanyChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      fullWidth
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => {
        setDebouncedSearch(undefined);
        onBlur && onBlur();
      }}
      searchable
      inline={inline}
      inset={inline}
      error={error}
      required={required}
      clearable={clearable}
      placeholder={emptyOptionText}
      // onAdd={onAddClick}
      isMulti={isMulti}
      maxDropdownHeight={maxDropdownHeight}
      smallContainer={smallContainer}
      dropdownOffset={dropdownOffset}
    />
  );
}
